/**
 * Planning V1 pure model. A read-only projection over the existing Project
 * hierarchy; nothing here is stored.
 *
 *   Project   -> projects.estimated_end_date          (Target Completion)
 *   Location  -> project_locations.target_completion_date
 *   Feature   -> project_features.target_completion_date
 *   Task      -> project_tasks.due_date
 *
 * A Task sits at its MOST SPECIFIC real scope and is never duplicated:
 *   feature_id            -> under that Feature
 *   location_id only      -> under that Location
 *   neither               -> under the Project
 * (a Task whose feature is archived / not visible falls back to its Location,
 * then the Project). Within a parent the order is: its own Tasks first, then
 * child entities; each group is earliest date first, undated last. Dates are date-only and are compared by
 * calendar day index (UTC arithmetic over Y-M-D parts), never by Date-time,
 * so there is no timezone shift.
 *
 * Task WORK SPAN (V1 scheduling foundation): Start -> Expected Completion,
 * where Expected = Start + Planned Duration + Organization Work Schedule
 * (helpers/tasks/workSchedule.js). It is drawn as the working days only
 * (non-working weekdays / dates are gaps). Due is an independent deadline and
 * never sets the span. No Start, no Duration or no known schedule -> no span
 * (never invented). Progress never moves a date.
 */
import { SCHEDULE_STATUS, computeExpectedCompletion, isWorkingDate, MAX_SEARCH_DAYS } from "../tasks/workSchedule";

const DAY_MS = 86400000;
const ISO = /^(\d{4})-(\d{2})-(\d{2})/;

export const PLAN_KIND = Object.freeze({
  PROJECT: "project",
  LOCATION: "location",
  FEATURE: "feature",
  TASK: "task",
});

export function dayIndex(value) {
  const match = ISO.exec(String(value ?? ""));
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const ms = Date.UTC(y, m - 1, d);
  return Number.isNaN(ms) ? null : Math.round(ms / DAY_MS);
}

export function dayIndexToISO(index) {
  const date = new Date(index * DAY_MS);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** The user's local calendar day as YYYY-MM-DD. */
export function localTodayISO(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** 0 = Sunday ... 6 = Saturday for a day index. */
export function weekdayOf(index) {
  return new Date(index * DAY_MS).getUTCDay();
}

const PLANNABLE_STATUS = new Set(["active", "on_hold"]);

export function isPlannableProject(project) {
  if (!project?.project_id) return false;
  if (project.is_active === false) return false;
  const status = project.status || "active";
  return PLANNABLE_STATUS.has(status);
}

function byDateThenOrder(a, b) {
  const da = a.dayIndex;
  const db = b.dayIndex;
  if (da != null && db != null && da !== db) return da - db;
  if (da != null && db == null) return -1;
  if (da == null && db != null) return 1;
  return (a.sort ?? 0) - (b.sort ?? 0);
}

/** Span state for a task row. */
export const WORK_SPAN = Object.freeze({
  OK: "ok", // Start + Duration + known schedule -> working-day segments
  NEEDS_INPUT: "needs_input", // no Start or no Duration -> no span
  SCHEDULE_UNAVAILABLE: "schedule_unavailable", // work schedule not loaded -> no span
  UNAVAILABLE: "unavailable", // the schedule cannot place it (no working day in bound)
});

/**
 * Working-day segments from Start through Expected Completion (inclusive),
 * as day indexes. Consecutive working days merge into one segment; a
 * non-working weekday or date ends a segment (a gap).
 */
export function workSegments(startISO, expectedISO, schedule) {
  const from = dayIndex(startISO);
  const to = dayIndex(expectedISO);
  if (from == null || to == null || to < from || !schedule) return [];
  const holidays = new Set((schedule.nonWorkingDates || []).map((d) => d.date));
  const out = [];
  let open = null;
  for (let i = from; i <= to && i - from <= MAX_SEARCH_DAYS; i += 1) {
    if (isWorkingDate(dayIndexToISO(i), schedule, holidays)) {
      if (open) open.end = i;
      else {
        open = { start: i, end: i };
        out.push(open);
      }
    } else {
      open = null;
    }
  }
  return out;
}

/** Start / Duration / Expected for one task row (display only; never stored). */
export function taskWorkSpan(task, workSchedule) {
  const startDate = task?.start_date || null;
  const minutes = Number(task?.planned_duration_minutes) > 0 ? Number(task.planned_duration_minutes) : null;
  const base = { startDate, durationMinutes: minutes, expectedDate: null, expectedDayIndex: null, segments: [] };
  if (!startDate || dayIndex(startDate) == null || !minutes) return { ...base, status: WORK_SPAN.NEEDS_INPUT };
  if (!workSchedule || workSchedule.status === SCHEDULE_STATUS.UNKNOWN || !workSchedule.schedule) {
    return { ...base, status: WORK_SPAN.SCHEDULE_UNAVAILABLE };
  }
  const expected = computeExpectedCompletion({ startDate, durationMinutes: minutes }, workSchedule.schedule);
  if (!expected) return { ...base, status: WORK_SPAN.UNAVAILABLE };
  return {
    ...base,
    status: WORK_SPAN.OK,
    expectedDate: expected,
    expectedDayIndex: dayIndex(expected),
    segments: workSegments(startDate, expected, workSchedule.schedule),
  };
}

function progressOf(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}

function sortKey(row, fallback) {
  return Number.isFinite(Number(row?.sort_order)) ? Number(row.sort_order) : fallback;
}

/**
 * Build the tree. Archived / inactive rows are dropped; rows whose project is
 * not in `projects` (not visible to the actor, archived, completed) are never
 * attached, which is also what keeps another project's rows out.
 */
export function buildPlanningTree({
  projects = [],
  locations = [],
  features = [],
  tasks = [],
  today,
  workSchedule = null,
}) {
  const todayIdx = dayIndex(today);
  const plannable = projects.filter(isPlannableProject);
  const byProject = new Map(plannable.map((p) => [p.project_id, p]));

  const featuresByLocation = new Map();
  features
    .filter((f) => f && f.is_active !== false && byProject.has(f.project_id))
    .forEach((f, i) => {
      const list = featuresByLocation.get(f.project_location_id) || [];
      list.push({
        id: f.project_feature_id,
        kind: PLAN_KIND.FEATURE,
        name: f.name || "Feature",
        date: f.target_completion_date || null,
        dayIndex: dayIndex(f.target_completion_date),
        sort: sortKey(f, i),
        tasks: [],
        children: [],
      });
      featuresByLocation.set(f.project_location_id, list);
    });

  const tasksByFeature = new Map();
  const tasksByLocation = new Map();
  const tasksByProject = new Map();
  const featureIds = new Set([...featuresByLocation.values()].flat().map((f) => f.id));
  const locationIds = new Set(
    locations
      .filter((l) => l && l.is_active !== false && byProject.has(l.project_id))
      .map((l) => l.project_location_id),
  );
  tasks
    .filter((t) => t && t.is_active !== false && byProject.has(t.project_id))
    .forEach((t, i) => {
      const completed = t.status === "completed";
      const span = taskWorkSpan(t, workSchedule);
      const node = {
        id: t.project_task_id,
        kind: PLAN_KIND.TASK,
        name: t.title || "Task",
        date: t.due_date || null,
        dayIndex: dayIndex(t.due_date),
        completed,
        overdue: !completed && dayIndex(t.due_date) != null && todayIdx != null && dayIndex(t.due_date) < todayIdx,
        // V1 scheduling (display only; never changes a date). The work span is
        // Start -> Expected Completion on working days; Due stays the marker.
        startDate: span.startDate,
        startDayIndex: dayIndex(span.startDate),
        durationMinutes: span.durationMinutes,
        expectedDate: span.expectedDate,
        expectedDayIndex: span.expectedDayIndex,
        workSegments: span.segments,
        spanStatus: span.status,
        // Warn-only flag (never blocks, never moves a date); not raised once the task is completed.
        expectedAfterDue: !completed && Boolean(span.expectedDate && t.due_date && span.expectedDate > String(t.due_date).slice(0, 10)),
        progress: completed ? 100 : progressOf(t.progress_percent),
        sort: sortKey(t, i),
        children: [],
      };
      const featureId = t.feature_id ?? null; // older cached tasks have none
      let bucket;
      let key;
      if (featureId && featureIds.has(featureId)) {
        bucket = tasksByFeature;
        key = featureId;
      } else if (t.location_id && locationIds.has(t.location_id)) {
        bucket = tasksByLocation;
        key = t.location_id;
      } else {
        bucket = tasksByProject;
        key = t.project_id;
      }
      const list = bucket.get(key) || [];
      list.push(node);
      bucket.set(key, list);
    });
  tasksByFeature.forEach((list, key) => list.sort(byDateThenOrder));
  featuresByLocation.forEach((list) =>
    list.forEach((feature) => {
      feature.tasks = tasksByFeature.get(feature.id) || [];
      feature.children = feature.tasks;
    }),
  );

  const locationsByProject = new Map();
  locations
    .filter((l) => l && l.is_active !== false && byProject.has(l.project_id))
    .forEach((l, i) => {
      const fs = (featuresByLocation.get(l.project_location_id) || []).sort(byDateThenOrder);
      const ts = (tasksByLocation.get(l.project_location_id) || []).sort(byDateThenOrder);
      const node = {
        id: l.project_location_id,
        kind: PLAN_KIND.LOCATION,
        name: l.name || "Location",
        date: l.target_completion_date || null,
        dayIndex: dayIndex(l.target_completion_date),
        sort: sortKey(l, i),
        features: fs,
        tasks: ts,
        children: [...ts, ...fs],
      };
      const list = locationsByProject.get(l.project_id) || [];
      list.push(node);
      locationsByProject.set(l.project_id, list);
    });

  const nodes = plannable.map((p, i) => {
    const locs = (locationsByProject.get(p.project_id) || []).sort(byDateThenOrder);
    const general = (tasksByProject.get(p.project_id) || []).sort(byDateThenOrder);
    const status = p.status || "active";
    const idx = dayIndex(p.estimated_end_date);
    return {
      id: p.project_id,
      kind: PLAN_KIND.PROJECT,
      name: p.project_name || p.name || "Project",
      date: p.estimated_end_date || null,
      dayIndex: idx,
      overdue: idx != null && todayIdx != null && idx < todayIdx,
      status,
      sort: i,
      locations: locs,
      tasks: general,
      children: [...general, ...locs],
    };
  });

  // Scheduled projects first (earliest target first), then unscheduled in
  // their existing (name) order. Stable.
  return nodes
    .map((node, i) => ({ node, i }))
    .sort((a, b) => {
      const da = a.node.dayIndex;
      const db = b.node.dayIndex;
      if (da != null && db != null) return da - db || a.i - b.i;
      if (da != null) return -1;
      if (db != null) return 1;
      return a.i - b.i;
    })
    .map(({ node }) => node);
}

function countDescendants(node) {
  const locations = node.locations || [];
  const features = locations.reduce((n, l) => n + l.features.length, 0);
  const tasks =
    (node.tasks || []).length +
    locations.reduce(
      (n, l) => n + l.tasks.length + l.features.reduce((m, f) => m + f.tasks.length, 0),
      0,
    );
  return { locations: locations.length, features, tasks };
}

export function describeNode(node) {
  if (node.kind === PLAN_KIND.PROJECT) {
    const c = countDescendants(node);
    return `${plural(c.locations, "location")} · ${plural(c.tasks, "task")}`;
  }
  if (node.kind === PLAN_KIND.LOCATION) {
    const tasks = node.tasks.length + node.features.reduce((n, f) => n + f.tasks.length, 0);
    return `${plural(node.features.length, "feature")} · ${plural(tasks, "task")}`;
  }
  return "";
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** Flatten to visible rows. Expansion is by node id; everything starts collapsed. */
export function flattenPlanning(tree, expanded = new Set()) {
  const rows = [];
  const walk = (node, depth) => {
    const expandable = node.children.length > 0;
    rows.push({ node, depth, expandable, isOpen: expandable && expanded.has(`${node.kind}:${node.id}`) });
    if (expandable && expanded.has(`${node.kind}:${node.id}`)) {
      node.children.forEach((child) => walk(child, depth + 1));
    }
  };
  tree.forEach((node) => walk(node, 0));
  return rows;
}

export function allNodes(tree) {
  const out = [];
  const walk = (node) => {
    out.push(node);
    node.children.forEach(walk);
  };
  tree.forEach(walk);
  return out;
}

/**
 * Honest counts only. Open dated work: tasks that are not completed, and
 * active/on-hold projects. Locations and Features have no completion state,
 * so they are never called overdue and are not counted as this week/upcoming
 * once past; they do count toward This Week / Upcoming when still in the
 * future.
 */
export function summarizePlanning(tree, today) {
  const todayIdx = dayIndex(today);
  const weekEnd = todayIdx == null ? null : todayIdx + 6;
  const out = { overdue: 0, thisWeek: 0, upcoming: 0, noTarget: 0 };
  allNodes(tree).forEach((node) => {
    if (node.completed || node.contextOnly) return;
    if (node.dayIndex == null) {
      out.noTarget += 1;
      return;
    }
    if (todayIdx == null) return;
    if (node.dayIndex < todayIdx) {
      if (node.kind === PLAN_KIND.TASK || node.kind === PLAN_KIND.PROJECT) out.overdue += 1;
    } else if (node.dayIndex <= weekEnd) {
      out.thisWeek += 1;
    } else {
      out.upcoming += 1;
    }
  });
  return out;
}

/** Items missing a date, with their Project for context (compact section). */
export function collectUnscheduled(tree) {
  const items = [];
  const walk = (node, project, ctx = {}) => {
    const here = {
      locationId: node.kind === PLAN_KIND.LOCATION ? node.id : ctx.locationId,
      featureId: node.kind === PLAN_KIND.FEATURE ? node.id : ctx.featureId,
    };
    if (node.dayIndex == null && !node.completed && !node.contextOnly) {
      items.push({
        node,
        projectName: node.kind === PLAN_KIND.PROJECT ? "" : project.name,
        // Real record identity (for opening the item); additive.
        target: { kind: node.kind, id: node.id, projectId: project.id, locationId: here.locationId, featureId: here.featureId },
      });
    }
    node.children.forEach((child) => walk(child, project, here));
  };
  tree.forEach((project) => walk(project, project));
  const counts = { project: 0, location: 0, feature: 0, task: 0 };
  items.forEach(({ node }) => {
    counts[node.kind] += 1;
  });
  return { items, counts, total: items.length };
}

/** Timeline window: around today, widened to cover every dated item. */
export function computeDateRange(tree, today, { pastDays = 14, futureDays = 60, cap = 400 } = {}) {
  const todayIdx = dayIndex(today);
  let start = todayIdx - pastDays;
  let end = todayIdx + futureDays;
  allNodes(tree).forEach((n) => {
    // Due / target dates, plus a task's work span (Start .. Expected).
    [n.dayIndex, n.workSegments?.length ? n.startDayIndex : null, n.workSegments?.length ? n.expectedDayIndex : null]
      .filter((v) => v != null)
      .forEach((idx) => {
        start = Math.min(start, idx - 3);
        end = Math.max(end, idx + 10);
      });
  });
  start = Math.max(start, todayIdx - cap);
  end = Math.min(end, todayIdx + cap);
  return { start, end, days: end - start + 1, todayIdx };
}

/**
 * Name search for the desktop toolbar. A node that matches keeps its whole
 * subtree; otherwise only matching descendants (and their ancestors) are kept.
 * Pure: returns new nodes, never mutates the tree.
 */
export function filterPlanningTree(tree, term) {
  const needle = String(term || "").trim().toLowerCase();
  if (!needle) return tree;
  const matches = (node) => String(node.name || "").toLowerCase().includes(needle);
  const prune = (node) => {
    if (matches(node)) return node;
    const tasks = (node.tasks || []).map(prune).filter(Boolean);
    const features = (node.features || []).map(prune).filter(Boolean);
    const locations = (node.locations || []).map(prune).filter(Boolean);
    if (!tasks.length && !features.length && !locations.length) return null;
    const next = { ...node, tasks };
    if (node.kind === PLAN_KIND.PROJECT) {
      next.locations = locations;
      next.children = [...tasks, ...locations];
    } else if (node.kind === PLAN_KIND.LOCATION) {
      next.features = features;
      next.children = [...tasks, ...features];
    } else {
      next.children = tasks;
    }
    return next;
  };
  return tree.map(prune).filter(Boolean);
}

/**
 * Keep a node when `predicate(node)` is true or any descendant is kept
 * (ancestors stay for context). Used by the desktop KPI focus. Pure.
 */
export function pruneByPredicate(tree, predicate) {
  const prune = (node) => {
    const tasks = (node.tasks || []).map(prune).filter(Boolean);
    const features = (node.features || []).map(prune).filter(Boolean);
    const locations = (node.locations || []).map(prune).filter(Boolean);
    if (!predicate(node) && !tasks.length && !features.length && !locations.length) return null;
    const next = { ...node, tasks };
    if (node.kind === PLAN_KIND.PROJECT) {
      next.locations = locations;
      next.children = [...tasks, ...locations];
    } else if (node.kind === PLAN_KIND.LOCATION) {
      next.features = features;
      next.children = [...tasks, ...features];
    } else {
      next.children = tasks;
    }
    return next;
  };
  return tree.map(prune).filter(Boolean);
}

/** The same classification summarizePlanning counts, per node, for KPI focus. */
export function planningBucket(node, today) {
  const todayIdx = dayIndex(today);
  if (node.completed || node.contextOnly) return null;
  if (node.dayIndex == null) return "none";
  if (todayIdx == null) return null;
  if (node.dayIndex < todayIdx) return node.kind === PLAN_KIND.TASK || node.kind === PLAN_KIND.PROJECT ? "overdue" : null;
  return node.dayIndex <= todayIdx + 6 ? "week" : "upcoming";
}

/**
 * Where the desktop timeline should land when a KPI card is focused: the
 * oldest overdue date, the first upcoming date, or today for This Week
 * (Planning's own week: today through today + 6). Real stored dates only;
 * null when nothing is dated in that bucket (or for No Target Date).
 */
export function focusTargetDay(tree, focus, today) {
  const todayIdx = dayIndex(today);
  if (!focus || focus === "none" || todayIdx == null) return null;
  if (focus === "week") return todayIdx;
  const days = allNodes(tree)
    .filter((node) => planningBucket(node, today) === focus)
    .map((node) => node.dayIndex);
  return days.length ? Math.min(...days) : null;
}

/**
 * Planning filter hierarchy: All Projects -> Project -> Location -> Feature,
 * over the canonical tree (which only holds the actor's visible, active rows).
 *
 * Returns the effective selection (a stale or inaccessible id resolves to
 * "", and an upstream change therefore clears incompatible downstream ids),
 * the options for each level (Location only once a Project is chosen,
 * Feature only once a Location is chosen), and the scoped tree.
 *
 * In the scoped tree the ancestors of the selected Location / Feature stay
 * for context only (`contextOnly`): their own target dates and their other
 * children are dropped, so no view, summary or count shows anything outside
 * the selected scope.
 */
export function scopePlanning(tree = [], { projectId = "", locationId = "", featureId = "" } = {}) {
  const byName = (a, b) => String(a.name).localeCompare(String(b.name), "en", { sensitivity: "base" });
  const projectOptions = tree.map((n) => ({ id: n.id, name: n.name })).sort(byName);
  const project = projectId ? tree.find((n) => n.id === projectId) || null : null;
  const locationOptions = project ? project.locations.map((l) => ({ id: l.id, name: l.name })) : [];
  const location = project && locationId ? project.locations.find((l) => l.id === locationId) || null : null;
  const featureOptions = location ? location.features.map((f) => ({ id: f.id, name: f.name })) : [];
  const feature = location && featureId ? location.features.find((f) => f.id === featureId) || null : null;
  const selection = { projectId: project ? project.id : "", locationId: location ? location.id : "", featureId: feature ? feature.id : "" };

  const asContext = (node, extra) => ({ ...node, ...extra, contextOnly: true, date: null, dayIndex: null, overdue: false, tasks: [] });
  let scoped = tree;
  if (project && !location) {
    scoped = [project];
  } else if (project && location && !feature) {
    scoped = [asContext(project, { locations: [location], children: [location] })];
  } else if (project && location && feature) {
    const loc = asContext(location, { features: [feature], children: [feature] });
    scoped = [asContext(project, { locations: [loc], children: [loc] })];
  }
  return { selection, projectOptions, locationOptions, featureOptions, tree: scoped };
}
